<template>
  <Dropdown
    :modelValue="normalizedModelValue"
    :options="filteredItems"
    :optionLabel="optionName.label"
    :placeholder="isLoading && isEmpty ? 'Поиск вариантов...' : (isValueSetted ? 'Не выбрано' : 'Выберите значение')"
    :disabled="isDisabled || (isLoading && isEmpty)"
    :filter="!isDisabled && !notSearch"
    @filter="onFilterItems"
    :option-value="useIdOnly ? optionName.pk : undefined"
    :invalid="isInvalid"
    @show="showItems"
    @before-show="beforeShowItems"
    :showToggleAll="!isDisabled"
    @update:modelValue="
      ($event) => {
        emit('update:modelValue', $event);
        catalogDataSettings.isLoadingFirst = false;
      }
    "
    showClear
    :loading="isLoading"
    :emptyMessage="emptyMessageComputed"
    :emptyFilterMessage="emptyFilterMessageComputed"
  >
    <template #value="slotProps">
      <div
        v-if="isLoading"
        class="loading-value-placeholder"
        :class="
          props.modelValue && props.modelValue.length != 0 ? 'any-catalog' : ''
        "
      >
        &nbsp;
      </div>
      <div v-else-if="selectType == 'platform' && slotProps.value">
        <div
          class="chip-container"
          v-if="findItem(slotProps.value) != undefined"
        >
          <PlatformChip :icon-class="slotProps.value">
            {{ findItem(slotProps.value)["name"] }}</PlatformChip
          >
        </div>
      </div>
    </template>
    <template #option="slotProps">
      <div v-if="isLoading">
        <div><Skeleton width="10rem" class="mb-2"></Skeleton></div>
      </div>
      <div v-else-if="selectType == 'platform'">
        <div class="chip-container">
          <PlatformChip :icon-class="slotProps.option[optionName.pk]">
            {{ slotProps.option[optionName.label] }}</PlatformChip
          >
        </div>
      </div>
      <div v-else>
        {{ slotProps.option[optionName.label] }}
      </div>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import Dropdown from "primevue/dropdown";
import Skeleton from "primevue/skeleton";

import { ref, watch, onMounted, computed, nextTick } from "vue";
import { useCatalogData } from "./useCatalogData";
import PlatformChip from "@/shared/chips/PlatformChip.vue";

const props = defineProps([
  "modelValue",
  "isDisabled",
  "selectType",
  "useIdOnly",
  "isInvalid",
  "isValueSetted",
  "notSearch",
  "filterField",
  "filterValue",
  "filterList",
  "filterFields",
  "filterValues",
]);
const emit = defineEmits(["update:modelValue"]);

const catalogDataSettings = ref({
  isLoading: false,
  filterStr: "",
  debounceTimeout: null as number | null,
  isLoadingFirst: true,
  optionName: "",
  optionValue: "",
  isLoadedAll: false,
});

const {
  store,
  items,
  optionName,
  addScrollHundler,
  updateSettings,
  onFilterItems,
  getSortingArray,
  sendItemRequest,
  replaceItems,
  objectToValue,
} = useCatalogData(
  props.selectType,
  catalogDataSettings.value,
  props.useIdOnly
);

let element: Element | null = null;

const findItemByValue = (value: any) => {
  if (value == null) return null;

  const searchValue =
    typeof value === "object" ? value[optionName.value.pk] : value;

  return items.value.find((item: any) => {
    const itemValue = item[optionName.value.pk];
    return itemValue == searchValue;
  });
};

const loadMissingValue = (value: any) => {
  if (typeof value === "object" || value === null) return;

  catalogDataSettings.value.isLoading = true;
  store.getItems("", [objectToValue(value)], true, true).then(() => {
    setTimeout(() => {
      catalogDataSettings.value.isLoading = false;
    }, 200);
  });
};

const checkAndLoadValue = (value: any) => {
  if (value == null) return;

  const foundItem = findItemByValue(value);

  if (!foundItem) {
    if (typeof value === "object") {
      const pkValue = value[optionName.value.pk];
      if (pkValue != null) {
        loadMissingValue(pkValue);
      }
    } else {
      loadMissingValue(value);
    }
  }
};

watch(
  () => catalogDataSettings.value.filterStr,
  () => {
    store.sortItems(getSortingArray([props.modelValue]));
    if (catalogDataSettings.value.debounceTimeout)
      clearTimeout(catalogDataSettings.value.debounceTimeout);
    catalogDataSettings.value.debounceTimeout = setTimeout(
      sendItemRequest,
      1000,
      [props.modelValue]
    );
  },
  { immediate: true }
);

watch(
  () => catalogDataSettings.value,
  (newValue) => {
    updateSettings(newValue);
  }
);

watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    if (catalogDataSettings.value.isLoadingFirst && newValue != null) {
      catalogDataSettings.value.isLoading = true;
      store.getItems("", [objectToValue(newValue)], true, true).then(() => {
        catalogDataSettings.value.isLoadingFirst = false;
        setTimeout(() => {
          catalogDataSettings.value.isLoading = false;
        }, 200);
      });
    } else if (!catalogDataSettings.value.isLoadingFirst && newValue != null && newValue !== oldValue) {
      nextTick(() => {
        checkAndLoadValue(newValue);
      });
    }
  },
  { immediate: true }
);

watch(
  () => items.value.length,
  (itemsLength) => {
    if (
      itemsLength > 0 &&
      !catalogDataSettings.value.isLoadingFirst &&
      props.modelValue != null
    ) {
      checkAndLoadValue(props.modelValue);
    }
  }
);

onMounted(() => {
  if (!store.itemList.length) {
    catalogDataSettings.value.isLoading = true;
    store
      .getItems(
        "",
        getSortingArray([props.modelValue]),
        true,
        true,
        0,
        50,
        true
      )
      .then(() => {
        catalogDataSettings.value.isLoadingFirst = false;
        catalogDataSettings.value.isLoading = false;
        checkAndLoadValue(props.modelValue);
      })
      .catch(() => {
        catalogDataSettings.value.isLoadingFirst = false;
        catalogDataSettings.value.isLoading = false;
      });
  } else if (store.isLoadingInitial && store.initialLoadPromise) {
    catalogDataSettings.value.isLoading = true;
    store.initialLoadPromise.then(() => {
      catalogDataSettings.value.isLoadingFirst = false;
      catalogDataSettings.value.isLoading = false;
      checkAndLoadValue(props.modelValue);
    })
    .catch(() => {
      catalogDataSettings.value.isLoadingFirst = false;
      catalogDataSettings.value.isLoading = false;
    });
  } else {
    catalogDataSettings.value.isLoadingFirst = false;
    nextTick(() => {
      checkAndLoadValue(props.modelValue);
    });
  }
});

const isLoading = computed(() => {
  const loading = catalogDataSettings.value.isLoading || catalogDataSettings.value.isLoadingFirst;
  return loading;
});

const isEmpty = computed(() => {
  return filteredItems.value.length === 0;
});

const emptyMessageComputed = computed(() => {
  return isLoading.value ? 'Поиск вариантов...' : 'Нет доступных вариантов';
});

const emptyFilterMessageComputed = computed(() => {
  return isLoading.value ? 'Поиск вариантов...' : 'Результатов не найдено';
});

const normalizedModelValue = computed(() => {
  if (props.modelValue == null) return null;

  if (props.useIdOnly) {
    return props.modelValue;
  }

  if (typeof props.modelValue === "object") {
    const pkValue = props.modelValue[optionName.value.pk];
    if (pkValue != null) {
      const foundItem = items.value.find((item: any) => {
        return item[optionName.value.pk] == pkValue;
      });
      if (foundItem) {
        return foundItem;
      }
      return props.modelValue;
    }
  }

  return props.modelValue;
});

const filteredItems = computed(() => {
  if (props.filterField && props.filterValue) {
    return items.value.filter(
      (item: any) => item[props.filterField] == props.filterValue
    );
  }

  if (props.filterList && props.filterField) {
    return items.value.filter(
      (item: any) => !props.filterList.includes(item[props.filterField])
    );
  }

  if (
    props.filterFields &&
    props.filterValues &&
    props.filterFields.length == props.filterValues.length
  ) {
    let newItems = items.value;
    for (let i = 0; i < props.filterFields.length; i++) {
      newItems = newItems.filter(
        (item: any) => item[props.filterFields[i]] == props.filterValues[i]
      );
    }
    return newItems;
  }
  return items.value;
});

const beforeShowItems = () => {
  store.sortItems(getSortingArray([props.modelValue]));
};

const showItems = () => {
  element = document.querySelectorAll(
    ".p-dropdown-panel .p-dropdown-items-wrapper"
  )[0];
  if (element) addScrollHundler(element);
};

const findItem = (pk: string) => {
  return items.value.filter((e: any) => e[optionName.value.pk] == pk)[0];
};
</script>

<style lang="scss">
.loading-value-placeholder {
  line-height: 1.5;
  min-height: 1.5em;
  display: inline-block;
}

.any-catalog {
  padding: 0;
}

.p-dropdown-items-wrapper {
  padding-left: 4px;
  padding-top: 5px;
}

.chip-container {
  background-color: #eaeaf0;
  border-radius: 4px;
  width: fit-content;
}

.platform-chip {
  padding-top: 0 !important;
  padding-bottom: 0 !important;

  .text {
    color: $main-font-color !important;
  }
}
</style>
